import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload, ChevronDown } from 'lucide-react';
import { Recipe, TranslatedText, SupportedLanguage, ALL_LANGUAGES, translateTexts } from '../../services/recipes/recipesService';
import { cn } from '../../lib/utils';
import { secondaryText, smallTextSize } from '../../styling/stylingHelper';
import { DIETS, toTranslationKey } from '../../types/RecipeEnums';
import LanguageTabBar from './LanguageTabBar';
import AllergenDeclarationEditor, { LegacyAllergenWarnings } from './AllergenDeclarationEditor';
import { AllergenDraft, draftFromRecipe, getUnmappedLegacyAllergens, isSupportedAllergenDetails } from '../../types/RecipeAllergens';
import { allergenErrorKey } from '../../services/recipes/recipeAllergensService';
import { useRecipeStore } from '../../stores/appRecipeStore';

interface SaveRecipeModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  restaurantId: string;
  recipe?: Recipe;
  onSave: () => Promise<void>;
}

export default function SaveRecipeModal({
  isOpen,
  onClose,
  mode,
  restaurantId,
  recipe,
  onSave,
}: SaveRecipeModalProps) {
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const defaultLang = (['en', 'es', 'nl'].includes(i18n.language) ? i18n.language : 'en') as SupportedLanguage;
  const [activeLanguage, setActiveLanguage] = useState<SupportedLanguage>(defaultLang);
  const [isTranslating, setIsTranslating] = useState(false);

  const [nameTranslations, setNameTranslations] = useState<TranslatedText>({ en: '' });
  const [declaration, setDeclaration] = useState<AllergenDraft>(() => draftFromRecipe());
  const [allergensChanged, setAllergensChanged] = useState(false);
  const [expectedRevision, setExpectedRevision] = useState('');
  const createRecipe = useRecipeStore(s => s.createRecipeRecord);
  const updateRecipe = useRecipeStore(s => s.saveRecipeRecord);
  const [diets, setDiets] = useState<string[]>([]);
  const [isDietsDropdownOpen, setIsDietsDropdownOpen] = useState(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const dietsDropdownRef = useRef<HTMLDivElement>(null);
  const initializedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen) { initializedFor.current = null; return; }
    const key = `${mode}:${restaurantId}:${recipe?.id ?? ''}`;
    if (initializedFor.current === key) return;
    initializedFor.current = key;
    if (isOpen) {
      if (mode === 'edit' && recipe) {
        setNameTranslations(recipe.nameTranslations || { en: '' });
        setDeclaration(draftFromRecipe(recipe));
        setExpectedRevision(recipe.allergenDetails?.revision ?? '');
        setDiets(recipe.diets || []);
        setExistingImages(recipe.images || []);
        setImageFiles([]);
      } else {
        setNameTranslations({ en: '' });
        setDeclaration(draftFromRecipe());
        setExpectedRevision('');
        setDiets([]);
        setExistingImages([]);
        setImageFiles([]);
      }
      setActiveLanguage(defaultLang);
      setAllergensChanged(false);
      setIsDietsDropdownOpen(false);
      setError(null);
      setSaving(false);
    }
  }, [isOpen, mode, restaurantId, recipe, defaultLang]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dietsDropdownRef.current && !dietsDropdownRef.current.contains(event.target as Node)) {
        setIsDietsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasContent = {
    en: !!nameTranslations.en,
    es: !!nameTranslations.es,
    nl: !!nameTranslations.nl,
  };

  const handleTranslate = async () => {
    const src = activeLanguage;
    const srcName = nameTranslations[src];
    if (!srcName) return;

    const targets = ALL_LANGUAGES.filter(l => l !== src);
    setIsTranslating(true);
    setError(null);

    try {
      const { translations } = await translateTexts(
        [{ key: 'recipe_name', value: srcName }],
        src,
        targets
      );

      if (translations['recipe_name']) {
        const updated = { ...nameTranslations };
        for (const tl of targets) {
          updated[tl] = translations['recipe_name'][tl] || '';
        }
        setNameTranslations(updated);
      }
    } catch (err: any) {
      console.error('Translation error:', err);
      setError(err.message || 'Translation failed');
    } finally {
      setIsTranslating(false);
    }
  };

  const handleToggleDiet = (diet: string) => {
    if (diets.includes(diet)) {
      setDiets(diets.filter(d => d !== diet));
    } else {
      setDiets([...diets, diet]);
    }
  };

  const handleRemoveDiet = (diet: string) => {
    setDiets(diets.filter(d => d !== diet));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setImageFiles(prev => [...prev, ...filesArray]);
    }
  };

  const handleRemoveNewImage = (index: number) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const currentName = nameTranslations[activeLanguage] || nameTranslations.en || '';
    if (!currentName.trim()) {
      setError(t('recipes.nameRequired') || 'Recipe name is required');
      return;
    }

    // Ensure en is populated
    const finalNameTranslations = { ...nameTranslations };
    if (!finalNameTranslations.en) {
      finalNameTranslations.en = nameTranslations[activeLanguage] || '';
    }
    // Trim en
    finalNameTranslations.en = finalNameTranslations.en.trim();

    setSaving(true);
    setError(null);

    try {
      if (mode === 'create') {
        await createRecipe(
          {
            userId: '', // Will be set by backend from auth
            restaurantId,
            nameTranslations: finalNameTranslations,
            images: [],
            ...(allergensChanged ? { allergenDetails: declaration } : {}),
            diets,
            components: [],
          },
          imageFiles
        );
      } else if (recipe) {
        await updateRecipe(
          {
            id: recipe.id,
            nameTranslations: finalNameTranslations,
            ...(allergensChanged ? { allergenDetails: declaration, expectedAllergenRevision: expectedRevision } : {}),
            diets,
          },
          imageFiles.length > 0 ? imageFiles : undefined
        );
      }
      await onSave();
      onClose();
    } catch (err: any) {
      console.error(err);
      setError(t(allergenErrorKey(err)));
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const lang = activeLanguage;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50" onClick={onClose}>
      <div
        className="bg-card dark:bg-card-dark rounded-3xl shadow-2xl w-full max-w-2xl border border-zinc-200 dark:border-white/20 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-50">
              {mode === 'create' ? t('recipes.createRecipe') : t('recipes.editRecipe')}
            </h2>
            <button
              onClick={onClose}
              className="flex-none p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
              aria-label={t('common.close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Language Tab Bar */}
          <LanguageTabBar
            activeLanguage={activeLanguage}
            onChangeLanguage={setActiveLanguage}
            onTranslate={handleTranslate}
            isTranslating={isTranslating}
            hasContent={hasContent}
          />

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-6">
            {/* Recipe Name */}
            <div>
              <label className={cn(smallTextSize, secondaryText, 'block mb-2')}>
                {t('recipes.recipeName')} *
              </label>
              <input
                type="text"
                value={nameTranslations[lang] || ''}
                onChange={(e) => setNameTranslations({ ...nameTranslations, [lang]: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-background dark:bg-background-dark text-text dark:text-text-dark"
                placeholder={t('recipes.enterRecipeName')}
              />
            </div>

            {/* Images */}
            <div>
              <label className={cn(smallTextSize, secondaryText, 'block mb-2')}>
                {t('recipes.images')}
              </label>

              {/* Existing Images (edit mode) */}
              {mode === 'edit' && existingImages.length > 0 && (
                <div className="mb-3">
                  <p className={cn('text-xs', secondaryText, 'mb-2')}>Current images:</p>
                  <div className="flex gap-2 flex-wrap">
                    {existingImages.map((url, index) => (
                      <img
                        key={index}
                        src={url}
                        alt={`Recipe ${index + 1}`}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* New Images Preview */}
              {imageFiles.length > 0 && (
                <div className="mb-3">
                  <p className={cn('text-xs', secondaryText, 'mb-2')}>
                    {mode === 'edit' ? 'New images (will replace current):' : 'Selected images:'}
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {imageFiles.map((file, index) => (
                      <div key={index} className="relative">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`New ${index + 1}`}
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => handleRemoveNewImage(index)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 dark:bg-red-600 text-white dark:text-white rounded-full hover:bg-red-600 dark:hover:bg-red-700 transition"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload Button */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 px-4 py-2 border border-zinc-300 dark:border-zinc-700 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
              >
                <Upload className="w-4 h-4" />
                <span className={cn(smallTextSize)}>{t('recipes.uploadImages')}</span>
              </button>
            </div>

            {/* Recipe-only declarations; unchanged edits omit this field entirely. */}
            <div>
              {mode === 'edit' && <LegacyAllergenWarnings allergies={recipe?.allergies} />}
              <AllergenDeclarationEditor value={declaration}
                disabled={saving || (mode === 'edit' && (!isSupportedAllergenDetails(recipe?.allergenDetails) || getUnmappedLegacyAllergens(recipe?.allergies).length > 0))}
                onChange={next => { setDeclaration(next); setAllergensChanged(true); }} />
              {mode === 'edit' && !isSupportedAllergenDetails(recipe?.allergenDetails) &&
                <p className="text-sm text-amber-800 dark:text-amber-300 mt-2">{t('recipeAllergens.unsupported')}</p>}
            </div>

            {/* Diets */}
            <div>
              <label className={cn(smallTextSize, secondaryText, 'block mb-2')}>
                {t('recipes.diets')}
              </label>

              {/* Multi-select dropdown */}
              <div className="relative" ref={dietsDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsDietsDropdownOpen(!isDietsDropdownOpen)}
                  className="w-full px-4 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-background dark:bg-background-dark text-text dark:text-text-dark flex items-center justify-between"
                >
                  <span className={diets.length === 0 ? secondaryText : ''}>
                    {diets.length === 0 ? t('recipes.selectDiets') : `${diets.length} selected`}
                  </span>
                  <ChevronDown className={cn('w-4 h-4 transition-transform', isDietsDropdownOpen && 'rotate-180')} />
                </button>

                {isDietsDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-card dark:bg-card-dark border border-zinc-300 dark:border-zinc-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {DIETS.map((diet) => (
                      <label
                        key={diet}
                        className="flex items-center gap-3 px-4 py-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={diets.includes(diet)}
                          onChange={() => handleToggleDiet(diet)}
                          className="w-4 h-4 rounded border-zinc-300 dark:border-zinc-600 text-zinc-900 dark:text-zinc-100 focus:ring-zinc-500 dark:focus:ring-zinc-500"
                        />
                        <span className="text-sm text-text dark:text-text-dark">{t(`dietKeys.${toTranslationKey(diet)}`, diet)}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected diets tags */}
              {diets.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {diets.map((diet, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full text-sm"
                    >
                      {t(`dietKeys.${toTranslationKey(diet)}`, diet)}
                      <button
                        onClick={() => handleRemoveDiet(diet)}
                        className="hover:text-blue-900 dark:hover:text-blue-100"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-8">
            <button
              onClick={onClose}
              disabled={saving}
              className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !(nameTranslations[lang] || '').trim()}
              className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition disabled:opacity-50"
            >
              {saving
                ? t('common.saving')
                : mode === 'create'
                ? t('common.create')
                : t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
